import type { SmsIntegration, SmsProviderSummary } from '@chukua/common';
import { Alert, Drawer } from 'antd';
import { FormCardSkeleton } from '../../../../../shared/components/FormCardSkeleton';
import { useSmsIntegrationSheet } from '../hooks/use-sms-integration-sheet';
import { SmsIntegrationForm } from './SmsIntegrationForm';

interface SmsIntegrationSheetProps {
  providers: SmsProviderSummary[];
  /** The provider sending now, named when installing another would replace it. */
  sendingName: string | undefined;
  /** The provider being installed, or undefined for the settings of `integrationId`. */
  providerKey: string | undefined;
  integrationId: string | undefined;
  onClose: () => void;
  /** Called after a save, a switch-off or an uninstall, so the list behind the sheet shows it. */
  onChanged: () => void;
  onStopSending: (integration: SmsIntegration) => Promise<void>;
  onUninstall: (integration: SmsIntegration) => Promise<void>;
}

/** Install an SMS provider, or change one's settings: its own fields, a real test text before new credentials are saved, and write-only secrets. */
export function SmsIntegrationSheet({
  providers,
  sendingName,
  providerKey,
  integrationId,
  onClose,
  onChanged,
  onStopSending,
  onUninstall,
}: SmsIntegrationSheetProps) {
  const done = () => {
    onChanged();
    onClose();
  };
  const sheet = useSmsIntegrationSheet({ providers, providerKey, integrationId, onDone: done });
  const title = sheet.integration ? sheet.integration.displayName : `Add ${sheet.provider?.displayName ?? 'an SMS provider'}`;

  return (
    <Drawer open onClose={onClose} title={title} size={480}>
      {sheet.isLoading && <FormCardSkeleton label="Loading the SMS provider" />}
      {!sheet.isLoading && !sheet.provider && (
        <Alert type="error" showIcon title="This SMS provider isn't available" description="It may not be part of this release, or the link is out of date." />
      )}
      {!sheet.isLoading && sheet.provider && (
        <SmsIntegrationForm
          sheet={sheet}
          provider={sheet.provider}
          sendingName={sendingName}
          onStopSending={async (integration) => {
            await onStopSending(integration);
            onClose();
          }}
          onUninstall={async (integration) => {
            await onUninstall(integration);
            onClose();
          }}
        />
      )}
    </Drawer>
  );
}
